import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axiosMain from '../../services';
import { toast } from 'react-toastify';
import { withRouter } from 'react-router-dom';
import AuthLayout from '../Auth/AuthLayout';
import { PasswordRequirements, passwordIsStrong } from '../Auth/passwordStrength';

const EMAIL_OK = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function usernameLooksValid(raw) {
  const s = String(raw || '')
    .trim()
    .toLowerCase();
  return s.length >= 3 && s.length <= 32 && /^[a-z0-9_]+$/.test(s);
}

const Signup = (props) => {
  const [activeStep, setActiveStep] = useState(0);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errValue, setErrValue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);

  const emailOk = useMemo(() => EMAIL_OK.test(String(email).trim()), [email]);
  const nameOk = fullName.trim().length >= 2;
  const usernameOk = useMemo(() => usernameLooksValid(username), [username]);
  const pwStrong = passwordIsStrong(password);
  const confirmOk = confirmPassword.length > 0 && password === confirmPassword;
  const canSubmit = nameOk && emailOk && usernameOk && pwStrong && confirmOk && !loading;
  const STEPS = ['Account', 'Organization', 'Security'];

  const handleValidation = () => {
    if (!nameOk) {
      setErrValue('Full name is required (at least 2 characters).');
      return false;
    }
    if (!emailOk) {
      setErrValue('Enter a valid email address.');
      return false;
    }
    if (!usernameOk) {
      setErrValue(
        'Choose a username (3–32 characters: lowercase letters, numbers, underscores only).'
      );
      return false;
    }
    if (!pwStrong) {
      setErrValue('Please meet every password requirement below.');
      return false;
    }
    if (!confirmOk) {
      setErrValue('Password and confirmation must match.');
      return false;
    }
    setErrValue(null);
    return true;
  };

  const canGoNext = () => {
    if (activeStep === 0) return nameOk && emailOk && usernameOk;
    if (activeStep === 1) return true;
    return false;
  };

  const handleAccountContinue = async () => {
    setErrValue(null);
    if (!canGoNext()) return;
    setAvailabilityLoading(true);
    try {
      await axiosMain.post('/auth/check-signup-availability', {
        email: email.trim(),
        username: username.trim(),
      });
      setActiveStep(1);
    } catch (error) {
      const status = error.response && error.response.status;
      // Older deployed APIs may not expose this route yet; signup still checks duplicates.
      if (status === 404) {
        setActiveStep(1);
        return;
      }
      const msg =
        (error.response && error.response.data && error.response.data.message) ||
        'Could not verify email or username availability.';
      setErrValue(msg);
    } finally {
      setAvailabilityLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!handleValidation()) return;
    setLoading(true);
    setErrValue(null);
    try {
      await axiosMain.post('/auth/signup', {
        fullName: fullName.trim(),
        email: email.trim(),
        username: username.trim(),
        password,
        confirmPassword,
        organizationName: organizationName.trim() || undefined,
        roles: ['user'],
      });
      toast.success('Account created — check your email to verify.', {
        position: 'top-right',
        autoClose: 4000,
      });
      props.history.push({
        pathname: '/verify-email',
        state: { email: email.trim() },
      });
    } catch (error) {
      const msg =
        (error.response && error.response.data && error.response.data.message) ||
        'Sign up failed. This email may already be registered.';
      setErrValue(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      wide
      title="Create your account"
      footer={
        <span>
          Already have an account?{' '}
          <Link className="text-primary" to="/sign-in">
            Sign in
          </Link>
        </span>
      }
    >
      <p className="ace-auth-subtle mb-3">
        Register with your work email and a strong password.
      </p>
      <div className="d-flex mb-3" style={{ gap: 8 }}>
        {STEPS.map((step, idx) => (
          <div
            key={step}
            style={{
              flex: 1,
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 600,
              borderRadius: 999,
              padding: '7px 8px',
              background: idx <= activeStep ? '#DA2E09' : '#F1F3F5',
              color: idx <= activeStep ? '#fff' : '#6c757d',
            }}
          >
            {step}
          </div>
        ))}
      </div>

      {errValue ? <p style={{ color: 'red' }}>{errValue}</p> : null}

      {activeStep === 0 ? (
        <>
          <div className="form-group">
            <label>Full name</label>
            <input
              type="text"
              className="form-control"
              placeholder="Your name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              autoComplete="name"
            />
            {fullName.length > 0 && !nameOk ? (
              <small className="text-danger">At least 2 characters</small>
            ) : null}
          </div>
          <div className="form-group">
            <label>Work email</label>
            <input
              type="email"
              className="form-control"
              placeholder="you@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />
            {email.length > 0 && !emailOk ? (
              <small className="text-danger">Enter a valid email</small>
            ) : null}
          </div>
          <div className="form-group">
            <label>Username</label>
            <input
              type="text"
              className="form-control"
              placeholder="your_workspace_name"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              spellCheck={false}
            />
            {username.length > 0 && !usernameOk ? (
              <small className="text-danger">
                3–32 chars: lowercase letters, numbers, underscores
              </small>
            ) : null}
          </div>
        </>
      ) : null}
      {activeStep === 1 ? (
        <>
          <div
            style={{
              background: '#FFF4EE',
              border: '1px solid #FFD6C8',
              borderRadius: 10,
              padding: 10,
              marginBottom: 14,
              fontSize: 12,
            }}
          >
            Optional step: add your organization now, or skip and do it in setup wizard.
          </div>
          <div className="form-group">
            <label>Company / organization (optional)</label>
            <input
              type="text"
              className="form-control"
              placeholder="Company name"
              value={organizationName}
              onChange={(e) => setOrganizationName(e.target.value)}
              autoComplete="organization"
            />
          </div>
        </>
      ) : null}
      {activeStep === 2 ? (
        <>
          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              className="form-control"
              placeholder="Create a strong password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <PasswordRequirements password={password} confirmPassword={confirmPassword} />
          </div>
          <div className="form-group">
            <label>Confirm password</label>
            <input
              type="password"
              className="form-control"
              placeholder="Re-enter password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
            />
          </div>
        </>
      ) : null}
      <div className="d-flex justify-content-between align-items-center">
        <button
          type="button"
          className="btn btn-light"
          disabled={activeStep === 0 || loading || availabilityLoading}
          onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
        >
          Back
        </button>
        {activeStep < STEPS.length - 1 ? (
          <button
            type="button"
            className="btn btn-primary ace-auth-btn-primary"
            disabled={!canGoNext() || loading || availabilityLoading}
            onClick={() => {
              if (activeStep === 0) {
                handleAccountContinue();
              } else {
                setActiveStep((s) => Math.min(STEPS.length - 1, s + 1));
              }
            }}
          >
            {activeStep === 0 && availabilityLoading ? 'Checking…' : 'Continue'}
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary ace-auth-btn-primary"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {loading ? 'Creating…' : 'Create account'}
          </button>
        )}
      </div>
    </AuthLayout>
  );
};

export default withRouter(Signup);
